'use client';

import { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { AlertCircle } from 'lucide-react';

import { Label } from '@/components/ui/label/label';
import { Checkbox } from '@/components/ui/checkbox/checkbox';

import { useRegistrationStore } from '../../stores/registration-store';
import { useMarketingOptIns } from '../hooks/useMarketingOptIns';
import {
  RegistrationSummaryCard,
  type RegistrationSummaryCardProps,
} from '../components/RegistrationSummaryCard';
import type { TenantRegistrationConfig } from '../types';

interface PreferencesStepProps {
  config: TenantRegistrationConfig;
  eventId: string | null;
  /** Waitlist users skip payment — show the notice DGR shows */
  shouldSkipPaymentStep: boolean;
  summary: Omit<RegistrationSummaryCardProps, 'vehicleType'>;
}

/**
 * Step 3 — registration summary + newsletter and campaign-configured
 * marketing opt-ins (replaces DGR's hardcoded triumph/movember checkboxes).
 */
export function PreferencesStep({
  config,
  eventId,
  shouldSkipPaymentStep,
  summary,
}: PreferencesStepProps) {
  const t = useTranslations('register');

  const newsletterOptIn = useRegistrationStore((s) => s.newsletterOptIn);
  const setNewsletterOptIn = useRegistrationStore((s) => s.setNewsletterOptIn);
  const marketingOptIns = useRegistrationStore((s) => s.marketingOptIns);
  const setMarketingOptIn = useRegistrationStore((s) => s.setMarketingOptIn);

  const { optIns } = useMarketingOptIns(config.marketingOptIns, eventId);

  const showNewsletter = config.showNewsletterOptIn !== false;

  // When the newsletter checkbox is hidden, clear the store default (true) so we
  // don't persist implied consent the entrant never saw. Mirrors the seeding in
  // useMarketingOptIns; runs once on mount for tenants with it disabled.
  useEffect(() => {
    if (!showNewsletter) setNewsletterOptIn(false);
  }, [showNewsletter, setNewsletterOptIn]);

  return (
    <div>
      <h1 className="mb-2 text-3xl font-bold text-foreground md:text-4xl">{t('almostThere')}</h1>
      <p className="mb-8 text-muted-foreground">{t('reviewDetails')}</p>

      {shouldSkipPaymentStep && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 text-amber-600" />
            <p className="text-sm leading-relaxed text-amber-900">
              {t('waitlistSkipsPaymentNotice')}
            </p>
          </div>
        </div>
      )}

      <RegistrationSummaryCard {...summary} vehicleType={config.vehicleType} />

      {/* Stay updated */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="mb-4 font-semibold text-foreground">{t('stayUpdated')}</h3>
        <div className="space-y-4">
          {showNewsletter && (
            <div className="flex items-start gap-3">
              <Checkbox
                id="newsletter"
                checked={newsletterOptIn}
                onCheckedChange={(checked) => setNewsletterOptIn(checked === true)}
                className="mt-0.5"
              />
              <Label
                htmlFor="newsletter"
                className="cursor-pointer text-sm leading-relaxed text-muted-foreground"
              >
                {t('newsletterOptIn')}
              </Label>
            </div>
          )}
          {optIns.map((optIn) => (
            <div key={optIn.key} className="flex items-start gap-3">
              <Checkbox
                id={`marketing-${optIn.key}`}
                checked={marketingOptIns[optIn.key] ?? optIn.defaultChecked}
                onCheckedChange={(checked) => setMarketingOptIn(optIn.key, checked === true)}
                className="mt-0.5"
              />
              <Label
                htmlFor={`marketing-${optIn.key}`}
                className="cursor-pointer text-sm leading-relaxed text-muted-foreground"
              >
                {optIn.label}
              </Label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
